import React, { useCallback, useEffect, useState } from 'react'
import { useSelector } from 'hooks/use-redux'
import { isDeviceTypePhone } from '@disco/state-management/selectors/app'
type ContextType = {
  isHover: boolean
  setHover?: (isHover: boolean) => void
}
export const HoverContext: React.Context<ContextType> =
  React.createContext<ContextType>({
    isHover: false,
  })
type Props = {
  children: React.ReactNode
  enableTouchSupport?: boolean
}
/**
 * A provider component that wraps children with hover state management and mouse event handlers.
 * Provides hover context to child components and can optionally enable touch support on mobile devices.
 *
 * @param children - The child components to wrap with hover functionality
 * @param enableTouchSupport - Whether to enable touch support on mobile devices (forces hover state on phones)
 * @returns An array of wrapped child components with hover functionality
 */
export const HoverProvider = (
  props: Props,
): Array<NonNullable<React.ReactNode>> => {
  const isPhone = useSelector(isDeviceTypePhone)
  const forcedHover = props.enableTouchSupport ? isPhone : false
  const [isHover, setHover] = useState<boolean>(forcedHover)
  const handleMouseEnter = useCallback(() => setHover(true), [])
  const handleMouseLeave = useCallback(() => setHover(false), [])
  const handleMouseOver = useCallback(() => {
    if (!isHover) {
      setHover(true)
    }
  }, [isHover])

  // A native HTML5 drag delivers no mouse events for its whole duration, and
  // after the release the browser does not recompute the hover chain until the
  // pointer next moves -- so the `mouseleave` that would clear this row never
  // arrives and it keeps its hover styling long after the drag ended.
  // `dragstart` is the earliest signal that the pointer stream is gone in every
  // engine (Chromium also fires `pointercancel` here, Firefox and WebKit do
  // not), and it is dispatched at the draggable ancestor rather than this div,
  // hence the window listener. Subscribing only while hovered keeps this to one
  // listener instead of one per rendered row.
  useEffect(() => {
    if (!isHover || forcedHover) return

    window.addEventListener('dragstart', handleMouseLeave, true)
    return () => {
      window.removeEventListener('dragstart', handleMouseLeave, true)
    }
  }, [isHover, forcedHover, handleMouseLeave])

  return (
    React.Children.map(props.children, (child: React.ReactNode) => (
      <HoverContext.Provider
        value={{
          isHover,
          setHover,
        }}
      >
        <div
          onMouseEnter={forcedHover ? undefined : handleMouseEnter}
          onMouseLeave={forcedHover ? undefined : handleMouseLeave}
          onMouseOver={forcedHover ? undefined : handleMouseOver}
        >
          {child}
        </div>
      </HoverContext.Provider>
    )) ?? []
  )
}
